
import {
  loadPdfImages,
  getPdfHeader,
  getPdfFooter,
  PDF_PAGE_MARGINS,
  createAndOpenPdf,
} from "../../../utility/pdfUtils";

// ─── Colors ───
const COLORS = {
  dark: "#333333",
  darkText: "#111111",
  gray: "#555555",
  lightGray: "#999999",
  border: "#cccccc",
  rowAlt: "#f7f7f7",
  white: "#ffffff",
};

// ─── Helper: safely extract display name ───
const safeDisplayName = (val) => {
  if (!val) return "-";
  if (typeof val === "string") return val;
  if (typeof val === "number") return String(val);
  if (val.name) return val.name;
  if (val.size) return val.size;
  if (val.label) return val.label;
  if (val.title) return val.title;
  return String(val);
};

// ─── Table Layouts ───
const thinBorderLayout = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => COLORS.border,
  vLineColor: () => COLORS.border,
  paddingLeft: () => 5,
  paddingRight: () => 5,
  paddingTop: () => 3,
  paddingBottom: () => 3,
};

const componentTableLayout = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: (i) => (i <= 1 ? "#999999" : "#dddddd"),
  vLineColor: () => COLORS.border,
  fillColor: (rowIndex) => {
    if (rowIndex === 0) return COLORS.dark;
    return rowIndex % 2 === 0 ? COLORS.rowAlt : null;
  },
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
};

// ─── Section Heading ───
const sectionHeading = (text) => ({
  table: {
    widths: ["*"],
    body: [
      [
        {
          text: text.toUpperCase(),
          bold: true,
          fontSize: 9.5,
          color: COLORS.white,
          fillColor: COLORS.dark,
          margin: [6, 3, 6, 3],
        },
      ],
    ],
  },
  layout: "noBorders",
  margin: [0, 0, 0, 3],
});

// ─── BOM Info Grid ───
const buildInfoGrid = (fields) => {
  const rows = [];
  for (let i = 0; i < fields.length; i += 4) {
    const rowFields = fields.slice(i, i + 4);
    while (rowFields.length < 4) rowFields.push({ label: "", value: "" });
    rows.push(
      rowFields.map((f) => ({
        columns: [
          {
            text: f.label,
            bold: true,
            fontSize: 7.5,
            color: COLORS.gray,
            width: 58,
          },
          {
            text: String(f.value ?? "—"),
            fontSize: 8.5,
            color: COLORS.darkText,
          },
        ],
        margin: [2, 2, 2, 2],
      }))
    );
  }
  return {
    table: {
      widths: ["25%", "25%", "25%", "25%"],
      body: rows,
    },
    layout: thinBorderLayout,
    margin: [0, 0, 0, 5],
  };
};

// ─── Component Table ───
const buildComponentTable = (components) => {
  const header = [
    { text: "Sr", bold: true, fontSize: 8, color: COLORS.white, alignment: "center" },
    { text: "Category", bold: true, fontSize: 8, color: COLORS.white },
    { text: "Product Type", bold: true, fontSize: 8, color: COLORS.white }, 
    { text: "Item Name", bold: true, fontSize: 8, color: COLORS.white },
    { text: "Unit", bold: true, fontSize: 8, color: COLORS.white, alignment: "center" },
    { text: "Qty", bold: true, fontSize: 8, color: COLORS.white, alignment: "center" },
  ];

  const rows =
    components.length > 0
      ? components.map((comp, index) => [
          { text: String(index + 1), fontSize: 8, alignment: "center", color: "#222222" },
          { text: safeDisplayName(comp.category), fontSize: 8, color: "#222222" },
           { text: safeDisplayName(comp.product_type), fontSize: 8, color: "#222222" },
          { text: safeDisplayName(comp.item), fontSize: 8, color: "#222222" },
          { text: safeDisplayName(comp.unit), fontSize: 8, alignment: "center", color: "#222222" },
          { text: String(comp.quantity || 0), fontSize: 8, bold: true, alignment: "center", color: "#222222" },
        ])
      : [
          [
            {
              text: "No components available",
              fontSize: 8,
              alignment: "center",
              color: "#999999",
              colSpan: 6,
              margin: [0, 4, 0, 4],
            },
            {}, {}, {}, {}, {},
          ],
        ];

  return {
    table: {
      headerRows: 1,
      widths: ["7%", "18%", "18%", "25%", "12%", "20%"],
      body: [header, ...rows],
    },
    layout: componentTableLayout,
    margin: [0, 0, 0, 6],
  };
};

// ─── Main Export ───
export const generateBOMPdf = async (bomData, action = "open") => {
  // ✅ Single line — uses shared cached loader from pdfUtils
  const { headerBase64, footerBase64 } = await loadPdfImages();

  const bom = bomData || {};

  // ✅ Safe array check
  const components = Array.isArray(bom.components) ? bom.components : [];

  const totalItems = components.length;
  const totalQty = components.reduce(
    (sum, c) => sum + Number(c.quantity || 0),
    0
  );
  const categories = [
    ...new Set(components.map((c) => safeDisplayName(c.category))),
  ].length;

  const createdOn = bom.created_at
    ? new Date(bom.created_at).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : bom.bom_date || "—";

  const infoFields = [
    { label: "BOM No.", value: bom.bom_no || "—" },
    { label: "BOM Date", value: bom.bom_date || "—" },
    { label: "BOM Name", value: bom.bom_name || "—" },
    { label: "Created On", value: createdOn },
    { label: "Components", value: totalItems },
    { label: "Total Qty", value: totalQty },
    { label: "Categories", value: categories },
    { label: "Status", value: "Active" },
  ];

  const docDefinition = {
    pageSize: "A4",

    // ✅ Shared margins from pdfUtils
    pageMargins: PDF_PAGE_MARGINS,

    // ✅ Removed font: "Roboto" — causes failure if not registered
    defaultStyle: {
      fontSize: 9,
      color: "#1a1a1a",
    },

    // ✅ Shared header & footer from pdfUtils — one liner each
    header: getPdfHeader(headerBase64),
    footer: getPdfFooter(footerBase64),

    content: [
      {
        text: "BILL OF MATERIAL (BOM)",
        bold: true,
        fontSize: 13,
        color: "#333333",
        alignment: "center",
        margin: [0, 0, 0, 8],
      },
      sectionHeading("BOM Information"),
      buildInfoGrid(infoFields),
      sectionHeading("Component List"),
      buildComponentTable(components),
    ],
  };

  // ✅ Shared PDF creator from pdfUtils
  createAndOpenPdf(
    docDefinition,
    action,
    `BOM_${bomData?.bom_no || "export"}`
  );
};